import React from 'react';
import type { ShowcasePopulatedItem, ItemImage } from 'root/apiTypes';
import { st, classes } from './Item.st.css';
import { useItemLogic } from './Item.logic';
import { Labels } from 'root/components/Menus/Labels/Labels';
import { PriceVariants } from 'root/components/Menus/PriceVariants/PriceVariants';
import { Text, Divider } from 'wix-ui-tpa/cssVars';
import { dataHooks } from 'root/dataHooks';
import { classes as global } from 'root/components/Menus/Global.st.css';
import { Dots } from 'root/components/Menus/Dots/Dots';
import { ModifierGroups } from 'root/components/Menus/ModifierGroups/ModifierGroups';
import { Featured } from 'root/assets/icons/featured/featured';
import { Image } from 'root/components/Menus/Image/Image';

export type ItemProps = {
  item: ShowcasePopulatedItem;
};

export const Item = ({ item }: ItemProps) => {
  const {
    hasPriceVariants,
    priceContent,
    shouldShowPrice,
    shouldShowLabels,
    shouldShowModifierGroups,
    itemImage,
    itemTitleHtmlTag,
    itemDescriptionHtmlTag,
    itemPriceHtmlTag,
    shouldOverrideItemDesign,
    isFeaturedItem,
    discountLabel,
    discountedPrice,
    variantDiscounts,
  } = useItemLogic({ item });

  return (
    <div className={classes.root} role="listitem">
      <div
        data-hook={dataHooks.item.container}
        className={st(
          classes.itemContainer,
          itemImage ? '' : classes.noImage,
          shouldOverrideItemDesign ? classes.featured : '',
          hasPriceVariants || !shouldShowPrice ? classes.noPrice : ''
        )}
      >
        <Divider className={classes.divider} />
        <div className={st(global.fullWidth, classes.itemNameContainer)}>
          {isFeaturedItem && (
            <Featured className={classes.featuredItemIcon} dataHook={dataHooks.item.featuredItemIcon} />
          )}
          <Text className={classes.itemName} data-hook={dataHooks.item.name} tagName={itemTitleHtmlTag}>
            {item.name}
          </Text>
        </div>
        {!hasPriceVariants && shouldShowPrice && <Dots />}
        {item.description && (
          <Text
            className={st(global.fullWidth, classes.itemDescription)}
            data-hook={dataHooks.item.description}
            tagName={itemDescriptionHtmlTag}
          >
            {item.description}
          </Text>
        )}
        {hasPriceVariants ? (
          discountLabel ? (
            <div className={classes.priceVariantsWithLabel}>
              <PriceVariants variants={item.priceVariants?.variants ?? []} variantDiscounts={variantDiscounts} />
              <Text className={classes.discountLabel} data-hook={dataHooks.item.discountLabel}>
                {discountLabel}
              </Text>
            </div>
          ) : (
            <PriceVariants variants={item.priceVariants?.variants ?? []} />
          )
        ) : (
          shouldShowPrice && (
            <div className={st(global.fullWidth, classes.itemPriceContainer)}>
              {discountedPrice ? (
                <div className={classes.discountContent}>
                  <div className={classes.discountedPriceRow}>
                    <Text
                      className={st(classes.itemPrice, classes.originalPrice)}
                      data-hook={dataHooks.item.price}
                      tagName={itemPriceHtmlTag}
                    >
                      {priceContent}
                    </Text>
                    <Text
                      className={classes.itemPrice}
                      data-hook={dataHooks.item.discountedPrice}
                      tagName={itemPriceHtmlTag}
                    >
                      {discountedPrice}
                    </Text>
                  </div>
                  {discountLabel && (
                    <Text className={classes.discountLabel} data-hook={dataHooks.item.discountLabel}>
                      {discountLabel}
                    </Text>
                  )}
                </div>
              ) : (
                <Text className={classes.itemPrice} data-hook={dataHooks.item.price} tagName={itemPriceHtmlTag}>
                  {priceContent}
                </Text>
              )}
            </div>
          )
        )}
        {shouldShowLabels && <Labels data-hook={dataHooks.item.labels} labels={item.labels!} />}
        {shouldShowModifierGroups && <ModifierGroups modifierGroups={item.modifierGroups ?? []} />}

        {itemImage ? (
          <Image<ItemImage>
            additionalImages={(item.additionalImages as ItemImage[]) ?? []}
            alt={item.image?.altText ?? item.name}
            dataHook={dataHooks.item.image}
            image={itemImage}
            imageClassName={classes.itemImage}
          />
        ) : null}
      </div>
    </div>
  );
};
